import { Card, CardContent, Grid2, Typography, Button } from '@mui/material';
import { UseLiquidacion } from '../hooks/useLiquidacion';
import { useEffect, useState, useMemo } from 'react';
import * as Yup from "yup";
import { HeaderComprobanteLiquidacion, RowComprobanteLiquidacion } from '../models/headerComprobanteLiquidacion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InputNumber from '../../../components/inputs/yup/materialui/inputNumber';
import InputFecha from '../../../components/inputs/yup/materialui/inputFecha';
import Tabla from '../../../components/ui/materialui/tabla';
import { useColumns } from '../utils/columnas';
import FooterLiquidacion from './footerLiquidacion';
import ModalComprobante from './modalComprobante';
import TableHeader from '../../../components/ui/materialui/tableHeader';
import ModalLiquidacion from './modalLiquidacion';
import { FormularioBeneficiarioModel } from '../models/formularioBeneficiarioModel';
import { numericFormatter } from 'react-number-format';
import ConfirmationModal from '../../../components/ui/materialui/confirmationModal';
import { ValuesTipoBeneficiario } from '../enums/tipoBeneficiario';
import { useToast } from '../../../hooks/useToast';
import { BeneficiarioModel } from '../models/beneficiarioModel';
import { ModalResultados } from '../../../components/ui/materialui/modalResultados';
import { useColumnsResults } from '../utils/columnasResults';
import { useColumnsResultsExcel } from '../utils/columnasResultsExcel';
import Conversiones from '../../../utils/conversiones';
import { descargarComprobanteLiquidacion } from '../../detalleMutuo/services/servicesReportes';
import { PermissionGuard } from '../../../components/auth/ProtectedRoute';
import { TipoFuncionalidad } from '../../../utils/enums';

interface IProps {
    idMutuo: number;
    isDesactivado?: boolean;
}

const DetalleLiquidacion: React.FC<IProps> = ({ idMutuo, isDesactivado }) => {

    const { esLiquidado, defaultValuesHeaderComprobante, fechaActivacion,
        calcularMontoAPagarLiquidacion, getEstadoMutuoLiquidacion, getDetalleLiquidacionMutuo, getFechaActivacionMutuo,
        agregarBeneficiario, actualizarBeneficiario, eliminarBeneficiario, liquidarMutuoEnLiquidacion, desliquidarMutuoEnLiquidacion } = UseLiquidacion();

    const [showComprobante, setShowComprobante] = useState<boolean>(false);
    const [showResult, setShowResult] = useState<boolean>(false);

    const [showFormularioBeneficiario, setShowFormularioBeneficiario] = useState<boolean>(false);
    const [rowSelected, setRowSelected] = useState<RowComprobanteLiquidacion | null>(null);
    const [isCreatingBeneficiario, setIsCreatingBeneficario] = useState<boolean>(false);

    const [montoADesembolsar, setMontoADesembolsar] = useState<number>(0);

    const [resultadosIntegracion, setResultadosIntegracion] = useState<BeneficiarioModel[]>([]);

    const { COLUMNS: COLUMNS_RESULT } = useColumnsResults();
    const { COLUMNSEXCEL } = useColumnsResultsExcel();

    const esMutuoLiquidado: boolean = !!esLiquidado;

    const { addToast } = useToast();

    const esFechaValida = (inputFecha: Date | null | undefined) => {
        if (inputFecha == undefined) return false;

        const fechaInputNormalizada = Conversiones.normalizarFecha(inputFecha);
        const fechaActivacionNormalizada = fechaActivacion ? Conversiones.normalizarFecha(fechaActivacion) : null;

        const isDateNull = fechaActivacion == null;
        const isDateAfterActivacion = !isDateNull && fechaInputNormalizada >= fechaActivacionNormalizada!;
        const isDateAfterActivacionComplete = isDateNull || isDateAfterActivacion;

        return isDateAfterActivacionComplete;
    };

    const calcularMontoADesembolsar = () => {
        if (defaultValuesHeaderComprobante.filasMutuosLiquidaciones) {
            let montoADesembolsar = 0;
            for (const fila of defaultValuesHeaderComprobante.filasMutuosLiquidaciones) {
                montoADesembolsar += fila.monto;
            }
            setMontoADesembolsar(montoADesembolsar);
        }
    }

    const schema = Yup.object<HeaderComprobanteLiquidacion>().shape({
        mutuoId: Yup.number().optional(),
        montoPago: Yup.number().required('Monto Requerido').test('igual-a-desembolsar', 'El Monto de pago debe coincidir con el monto a desembolsar',
            function (value) { return value === montoADesembolsar }
        ),
        fechaLiquidacion: Yup.date().required('Fecha requerida').test('isDateValid', 'Debe ser una fecha posterior a la fecha de activación', function (value) {
            return esFechaValida(value);
        })
    })

    const downloadComprobanteLiquidacion = (isExcel: boolean) => descargarComprobanteLiquidacion(idMutuo, isExcel);

    const {
        control,
        formState: { errors },
        reset,
        watch,
        setValue,
        handleSubmit,
        trigger
    } = useForm<HeaderComprobanteLiquidacion>({
        defaultValues: defaultValuesHeaderComprobante,
        resolver: yupResolver(schema),
        mode: 'onChange',
    })

    const fechaWatcher = watch("fechaLiquidacion")
    const montoWatcher = watch("montoPago")

    useEffect(() => {
        reset(defaultValuesHeaderComprobante);
        calcularMontoADesembolsar();
    }, [defaultValuesHeaderComprobante])

    useEffect(() => {
        getEstadoMutuoLiquidacion(idMutuo);
        getFechaActivacionMutuo(idMutuo);
        getDetalleLiquidacionMutuo(idMutuo, false, fechaWatcher, montoWatcher);
    }, [idMutuo])

    useEffect(() => {
        trigger("montoPago")
    }, [montoADesembolsar])

    const actualizarTabla = async (status: number) => {
        if (status === 200 || status === 201) {
            getDetalleLiquidacionMutuo(idMutuo, true, fechaWatcher, montoWatcher);
            setShowFormularioBeneficiario(false);
        }
    }

    const handleSubmitParticipante = async (rowInfo: FormularioBeneficiarioModel) => {
        const rowToSubmit: FormularioBeneficiarioModel = {
            rut: rowInfo.rut.replaceAll(".", ""),
            tipoBeneficario: rowInfo.tipoBeneficario === ValuesTipoBeneficiario.PARTICIPANTE ? ValuesTipoBeneficiario.PERSONA_NATURAL : rowInfo.tipoBeneficario,
            nombre: rowInfo.nombre,
            apellidoPaterno: rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INMOBILIARIA || rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INSTITUCION_FINANCIERA
                ? "" : rowInfo.apellidoPaterno,
            apellidoMaterno: rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INMOBILIARIA || rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INSTITUCION_FINANCIERA
                ? "" : rowInfo.apellidoMaterno,
            monto: rowInfo.monto,
            inmobiliaria: rowInfo.inmobiliaria,
            institucion: rowInfo.institucion,
            participante: rowInfo.participante
        }
        const response = await agregarBeneficiario(idMutuo, rowToSubmit);
        actualizarTabla(response.status);
    }

    const handleUpdateParticipante = async (rowInfo: FormularioBeneficiarioModel) => {
        const rowToSubmit: FormularioBeneficiarioModel = {
            rut: rowInfo.rut.replaceAll(".", ""),
            tipoBeneficario: rowInfo.tipoBeneficario === ValuesTipoBeneficiario.PARTICIPANTE ? ValuesTipoBeneficiario.PERSONA_NATURAL : rowInfo.tipoBeneficario,
            nombre: rowInfo.nombre,
            apellidoPaterno: rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INMOBILIARIA || rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INSTITUCION_FINANCIERA
                ? "" : rowInfo.apellidoPaterno,
            apellidoMaterno: rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INMOBILIARIA || rowInfo.tipoBeneficario === ValuesTipoBeneficiario.INSTITUCION_FINANCIERA
                ? "" : rowInfo.apellidoMaterno,
            monto: rowInfo.monto,
            inmobiliaria: rowInfo.inmobiliaria,
            institucion: rowInfo.institucion,
            participante: rowInfo.participante,
            rutOriginal: rowInfo.rutOriginal
        }
        const response = await actualizarBeneficiario(idMutuo, rowToSubmit);
        actualizarTabla(response.status);
    }

    const handleDeleteParticipante = async (rowInfo: FormularioBeneficiarioModel) => {
        rowInfo.rut = rowInfo.rut.replaceAll(".", "")
        const response = await eliminarBeneficiario(idMutuo, rowInfo.rut);
        actualizarTabla(response.status);
    }

    const [modalConfirmacion, setModalConfirmacion] = useState({
        show: false,
        title: '',
        message: '',
        metodo: () => {
        },
    });

    const showModalConfirmation = (rowToDelete: RowComprobanteLiquidacion) => {
        if (defaultValuesHeaderComprobante.filasMutuosLiquidaciones) {
            setModalConfirmacion({
                show: true,
                title: 'Eliminar beneficiario',
                message: `¿Estás seguro de eliminar el beneficiario ${rowToDelete.nombre} ${rowToDelete.apellidoPaterno} ${rowToDelete.apellidoMaterno}?`,
                metodo: () => {
                    const rowInfoMapped: FormularioBeneficiarioModel = {
                        nombre: rowToDelete?.nombre ?? "",
                        apellidoPaterno: rowToDelete?.apellidoPaterno ?? "",
                        apellidoMaterno: rowToDelete?.apellidoMaterno ?? "",
                        rut: rowToDelete?.rut ?? "",
                        monto: rowToDelete?.monto ?? 0,
                        tipoBeneficario: rowToDelete.tipoBeneficiario ?? "",
                        institucion: "",
                        inmobiliaria: "",
                        participante: ""
                    };
                    handleDeleteParticipante(rowInfoMapped);
                },
            });
        }
    }

    const calcularMonto = async (fechaLiquidacionInput: Date) => {
        const response = await calcularMontoAPagarLiquidacion(idMutuo, fechaLiquidacionInput)
        if (response.status === 200 || response.status === 201) {
            const valor = response.data.montoPago;
            setValue("montoPago", valor)
        }
        else {
            setValue("montoPago", 0)
        }
    }

    useEffect(() => {
        if (fechaWatcher && esFechaValida(fechaWatcher)) {
            calcularMonto(fechaWatcher);
        }
    }, [fechaWatcher, idMutuo])

    const generarLiquidacion = async () => {
        const response = await liquidarMutuoEnLiquidacion(idMutuo, fechaWatcher);
        if (response.status === 200 || response.status === 201) {
            addToast({
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
                mensaje: 'Crédito Liquidado Correctamente'
            });
            setResultadosIntegracion(response.data.respuestaIntegracionBeneficiarioDtos)
            getEstadoMutuoLiquidacion(idMutuo);
            getDetalleLiquidacionMutuo(idMutuo, true, fechaWatcher, montoWatcher);
        }
    }

    const generarDesliquidacion = async () => {
        const response = await desliquidarMutuoEnLiquidacion(idMutuo);
        if (response.status === 200 || response.status === 201) {
            addToast({
                mostrar: true,
                titulo: 'Confirmación',
                tipo: 'Success',
                mensaje: 'Crédito Desliquidado Correctamente'
            });
            getEstadoMutuoLiquidacion(idMutuo);
            getDetalleLiquidacionMutuo(idMutuo, true, fechaWatcher, montoWatcher);
        }
    }

    const submitLiquidacion = (form: HeaderComprobanteLiquidacion) => {
        setModalConfirmacion({
            show: true,
            title: 'Liquidar Mutuo',
            message: `¿Estás seguro de realizar la liquidación del mutuo?`,
            metodo: () => {
                generarLiquidacion()
            },
        });
    }

    const submitDesliquidacion = () => {
        setModalConfirmacion({
            show: true,
            title: 'Desliquidar Mutuo',
            message: `¿Estás seguro de desliquidar el mutuo?`,
            metodo: () => {
                generarDesliquidacion();
            },
        });
    }

    useEffect(() => {
        if (resultadosIntegracion.length > 0) {
            setShowResult(true)
        }
    }, [resultadosIntegracion])

    const { COLUMNS } = useColumns({
        control, setRowSelected: setRowSelected,
        setShowFormulario: setShowFormularioBeneficiario, isLiquidacion: esMutuoLiquidado,
        setIsCreating: setIsCreatingBeneficario, setShowEliminacion: showModalConfirmation
    });
    const columns = useMemo(() => COLUMNS, [defaultValuesHeaderComprobante]);

    return (
        <>

            {/* 
                [08-08-2025] Funcionalidad temporalmente deshabilitada:
                El componente <ModalComprobante> queda comentado debido a una incompatibilidad entre Aspose.PDF y entornos Linux con .NET 8. 
                Aspose.PDF utiliza internamente System.Drawing.Common, el cual no es compatible con Linux en esta versión del framework.
            */}
            {/* <ModalComprobante idMutuo={idMutuo} show={showComprobante} onHide={() => { setShowComprobante(false); }} /> */}



            <ModalLiquidacion idMutuo={idMutuo} show={showFormularioBeneficiario} onHide={() => { setShowFormularioBeneficiario(false) }}
                isCreating={isCreatingBeneficiario} rowInfo={rowSelected} onSubmit={handleSubmitParticipante} onUpdate={handleUpdateParticipante} />

            <ModalResultados
                open={showResult}
                onClose={() => setShowResult(false)}
                title='Resultados Integración con SANI'
                data={resultadosIntegracion}
                columns={COLUMNS_RESULT}
                columnsExcel={COLUMNSEXCEL}
                excelFileName='RESULTADOS-INTEGRACION-SANI'
            />
            <ConfirmationModal
                onHide={() =>
                    setModalConfirmacion({ ...modalConfirmacion, show: false })
                }
                onConfirm={modalConfirmacion.metodo}
                show={modalConfirmacion.show}
                message={modalConfirmacion.message}
                title={modalConfirmacion.title}
            />
            <form onSubmit={handleSubmit(submitLiquidacion)}>
                <Card sx={{ mb: 2, backgroundcolor: 'white' }} raised>
                    <CardContent>
                        <Grid2 container alignItems="center" spacing={2}>
                            <Grid2 size={2}>
                                <Typography className='card-title' variant="h6">Detalle Liquidación</Typography>
                            </Grid2>
                        </Grid2>

                        <Grid2 container spacing={2} sx={{ mt: 2 }}>
                            <Grid2 size={{ xs: 12, lg: 9 }}>
                                <InputNumber
                                    label="Monto a pagar"
                                    name="montoPago"
                                    readonly
                                    control={control}
                                    prefix='CLP'
                                    disabled
                                    decimales={3}
                                    error={errors.montoPago}
                                />
                            </Grid2>
                            <Grid2 size={{ xs: 12, lg: 3 }}>
                                <InputFecha
                                    label="Fecha de liquidación"
                                    name="fechaLiquidacion"
                                    esReadOnly={esMutuoLiquidado}
                                    control={control}
                                    fecha={defaultValuesHeaderComprobante.fechaLiquidacion}
                                    disabled={esMutuoLiquidado || isDesactivado}
                                    error={errors.fechaLiquidacion}
                                />
                            </Grid2>
                        </Grid2>

                        <Grid2 container spacing={2} sx={{ mt: 2 }} />
                        <Tabla
                            data={defaultValuesHeaderComprobante.filasMutuosLiquidaciones ?? []}
                            columns={columns}
                            titulo={
                                !esMutuoLiquidado ?
                                    <TableHeader
                                        button={
                                            <PermissionGuard permissions={[TipoFuncionalidad.Administrar_liquidacion]}>
                                                <Button variant="contained" disabled={isDesactivado} onClick={() => { setShowFormularioBeneficiario(true); setIsCreatingBeneficario(true) }}>
                                                    Agregar Beneficiario
                                                </Button>
                                                </PermissionGuard>}
                                    /> : <></>
                            }
                        />
                                                <Grid2 size={{ xs: 12, md: 4 }} sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
                                                    <Grid2 container spacing={1} sx={{ justifyContent: "flex-end", textAlign: "right", marginTop: "12px" }}>
                                                        <b>Monto a Desembolsar: {numericFormatter(montoADesembolsar.toString(),
                                                            { "thousandsGroupStyle": "thousand", "thousandSeparator": ".", "decimalSeparator": ",", "prefix": "CLP " })}</b>
                                                    </Grid2>
                                                </Grid2>
                                            </CardContent>
                </Card>
                <FooterLiquidacion idMutuo={idMutuo} esLiquidado={esMutuoLiquidado} onClickComprobante={() => { downloadComprobanteLiquidacion(true) }}
                    onClickDeshacer={submitDesliquidacion} isDesactivado={isDesactivado} />
            </form>
        </>
    )
}

export default DetalleLiquidacion;